import { useAuth } from '@clerk/clerk-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { spacesApi, transactionsApi } from '@/lib/api';
import type { MySpaceSendRules, SendToSpaceRequest, SpaceBatchPreview, SpaceExpense } from '@/types/spaces';
import { LONG_LIVED_SWR_OPTIONS, isSpaceSendingKey, useAuthedFetcher } from './swrConfig';
import { useSpaceWrite } from './useSpaceWrite';

const OPTIONS = { ...LONG_LIVED_SWR_OPTIONS, keepPreviousData: false };

export function useSendingTransactionLookup() {
  const { userId } = useAuth();
  const { enabled, scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  return useSWRMutation(['space-send-source', userId], async (_key, { arg }: { arg: string }) => {
    if (!enabled || scope !== 'personal' || !userId) throw new Error('O envio não está disponível.');
    return fetch((token) => transactionsApi.getOne(token, arg));
  });
}

export function useSpaceExpenseIndex() {
  const { userId } = useAuth();
  const { enabled, scope, spaces, listError } = useSpaceScope();
  const fetch = useAuthedFetcher();
  const ids = spaces.map((space) => space.id).sort();
  const key = enabled && scope === 'personal' && userId && !listError && ids.length ? ['space-expense-index', userId, ids] : null;
  const query = useSWR<SpaceExpense[], Error>(key, async () => {
    const lists = await Promise.all(ids.map((id) => fetch((token) => spacesApi.expenses(token, id))));
    return lists.flat().filter((expense) => !expense.removedAt);
  }, OPTIONS);
  return { ...query, data: key && !query.error ? query.data : undefined };
}

export function useMySpaceSendRules(active: boolean) {
  const { userId } = useAuth();
  const { enabled, scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  return useSWR<MySpaceSendRules, Error>(active && enabled && scope === 'personal' && userId ? ['space-send-rules', userId] : null,
    () => fetch(spacesApi.sendRules), OPTIONS);
}

export function useSpaceBatchPreview(spaceId: string | null, transactionId: string) {
  const { userId } = useAuth();
  const { enabled, scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  return useSWR<SpaceBatchPreview, Error>(enabled && scope === 'personal' && userId && spaceId ? ['space-send-preview', userId, spaceId, transactionId] : null,
    () => fetch((token) => spacesApi.previewSend(token, spaceId!, transactionId)), { ...OPTIONS, shouldRetryOnError: false });
}

export function useSendSpaceExpense(spaceId: string) {
  const { write, userId, mutate, refresh } = useSpaceWrite();
  return useSWRMutation(['space-send', userId, spaceId], async (_key, { arg }: { arg: SendToSpaceRequest }) => {
    const result = await write((token) => spacesApi.send(token, spaceId, arg));
    // Revalidate every membership-list variant, including other mounted transaction surfaces.
    void mutate((key) => isSpaceSendingKey(key, userId)).catch(() => undefined);
    refresh(['space-send-rules', userId]);
    return result;
  });
}
